/**
 * Shared comparison-summary builders for Payroll Dashboard PDF / Excel.
 * Uses analytics.compareSummary (preferred) or analytics.compare.
 */

import { statusBadgeLabel } from "./payrollDashboardAnalytics";

export const COMPARE_SUMMARY_SECTIONS = [
  {
    id: "pulse",
    title: "Pulse (cost / cash / net)",
    columns: [
      { key: "payMonth", label: "Month", money: false },
      { key: "status", label: "Status", money: false, format: (r) => statusBadgeLabel(r.status) },
      { key: "headcount", label: "HC", money: false },
      { key: "fixedCost", label: "Fixed", money: true },
      { key: "totalCost", label: "Payroll cost", money: true },
      { key: "totalCashFlow", label: "Cash outflow", money: true },
      { key: "netPay", label: "Net", money: true },
      { key: "grossPay", label: "Gross", money: true },
      { key: "avgFixedCost", label: "Avg fixed", money: true },
      { key: "avgEarnedCost", label: "Avg earned", money: true },
    ],
  },
  {
    id: "taxRegime",
    title: "Tax regimes",
    columns: [
      { key: "payMonth", label: "Month", money: false },
      { key: "taxOld", label: "Old count", money: false },
      { key: "taxNew", label: "New count", money: false },
      { key: "tdsOld", label: "TDS Old", money: true },
      { key: "tdsNew", label: "TDS New", money: true },
      { key: "tds", label: "TDS total", money: true },
    ],
  },
  {
    id: "statutory",
    title: "Statutory",
    columns: [
      { key: "payMonth", label: "Month", money: false },
      { key: "pfEmployer", label: "PF Er", money: true },
      { key: "esiEmployer", label: "ESI Er", money: true },
      { key: "pfEmployee", label: "PF Ee", money: true },
      { key: "esiEmployee", label: "ESI Ee", money: true },
      { key: "professionalTax", label: "PT", money: true },
      { key: "statutoryLiabilities", label: "Stat. liabilities", money: true },
      { key: "pfApplicable", label: "PF applicable HC", money: false },
      { key: "esiApplicable", label: "ESI applicable HC", money: false },
    ],
  },
  {
    id: "revisions",
    title: "Salary revisions",
    columns: [
      { key: "payMonth", label: "Month", money: false },
      { key: "revisionsCount", label: "Count", money: false },
      { key: "revisionsCtcDelta", label: "CTC delta", money: true },
      { key: "revisionArrears", label: "Arrears (register)", money: true },
    ],
  },
  {
    id: "fnf",
    title: "F&F / exits",
    columns: [
      { key: "payMonth", label: "Month", money: false },
      { key: "fnfCount", label: "Count", money: false },
      { key: "fnfNet", label: "Net", money: true },
      { key: "fnfLeaveEncashment", label: "Leave encash", money: true },
      { key: "fnfGratuity", label: "Gratuity", money: true },
      { key: "fnfNoticePay", label: "Notice pay", money: true },
      { key: "fnfNoticeRecovery", label: "Notice recovery", money: true },
    ],
  },
  {
    id: "attendance",
    title: "Attendance vs payroll",
    columns: [
      { key: "payMonth", label: "Month", money: false },
      { key: "attScope", label: "Att scope", money: false },
      { key: "attProcessed", label: "Att processed", money: false },
      { key: "attOpen", label: "Att open", money: false },
      { key: "attLocked", label: "Att locked", money: false },
      { key: "headcount", label: "Payroll HC", money: false },
      { key: "status", label: "Payroll status", money: false, format: (r) => statusBadgeLabel(r.status) },
    ],
  },
  {
    id: "deductions",
    title: "Encash / loans (register)",
    columns: [
      { key: "payMonth", label: "Month", money: false },
      { key: "leaveEncashCount", label: "Encash req", money: false },
      { key: "leaveEncashDays", label: "Encash days", money: false },
      { key: "loanDeductions", label: "Loan deductions", money: true },
      { key: "gratuityProvision", label: "Gratuity provision", money: true },
    ],
  },
];

/** Map UI aspect ids → summary section ids */
export function aspectsToSections(aspectIds = []) {
  const set = new Set(aspectIds || []);
  if (!set.size) return COMPARE_SUMMARY_SECTIONS;
  const map = {
    headcount: "pulse",
    fixedCost: "pulse",
    totalCost: "pulse",
    cash: "pulse",
    net: "pulse",
    tds: "taxRegime",
    taxRegime: "taxRegime",
    statutory: "statutory",
    masters: "pulse",
    fnf: "fnf",
    revisions: "revisions",
    attendance: "attendance",
  };
  const wanted = new Set();
  set.forEach((id) => {
    if (map[id]) wanted.add(map[id]);
  });
  // Always include pulse when any cost aspect selected
  if (
    ["headcount", "fixedCost", "totalCost", "cash", "net"].some((k) => set.has(k))
  ) {
    wanted.add("pulse");
  }
  const filtered = COMPARE_SUMMARY_SECTIONS.filter((s) => wanted.has(s.id));
  return filtered.length ? filtered : COMPARE_SUMMARY_SECTIONS;
}

export function resolveCompareSummaryRows(analytics) {
  if (analytics?.compareSummary?.length) return analytics.compareSummary;
  if (analytics?.compare?.length) {
    return analytics.compare.map((c) => ({
      payMonth: c.payMonth || c.label,
      status: c.status,
      headcount: c.headcount || 0,
      fixedCost: c.totals?.fixedCost || 0,
      totalCost: c.totals?.totalCost || 0,
      totalCashFlow: c.totals?.totalCashFlow || 0,
      netPay: c.totals?.netPay || 0,
      grossPay: c.totals?.grossPay || 0,
      tds: c.totals?.tds || 0,
      pfEmployer: c.totals?.pfEmployer || 0,
      esiEmployer: c.totals?.esiEmployer || 0,
      pfEmployee: c.totals?.pfEmployee || 0,
      esiEmployee: c.totals?.esiEmployee || 0,
      professionalTax: c.totals?.professionalTax || 0,
      statutoryLiabilities: c.totals?.statutoryLiabilities || 0,
      avgFixedCost: c.avgFixedCost || c.totals?.avgFixedCost || 0,
      avgEarnedCost: c.avgEarnedCost || c.totals?.avgEarnedCost || 0,
      taxOld: c.taxRegime?.oldCount || 0,
      taxNew: c.taxRegime?.newCount || 0,
      tdsOld: c.taxRegime?.tdsOld || 0,
      tdsNew: c.taxRegime?.tdsNew || 0,
      pfApplicable: c.statutory?.pfApplicable || 0,
      esiApplicable: c.statutory?.esiApplicable || 0,
      revisionsCount: 0,
      revisionsCtcDelta: 0,
      statutoryChangeCount: 0,
      fnfCount: 0,
      fnfNet: 0,
      fnfLeaveEncashment: 0,
      fnfGratuity: 0,
      fnfNoticePay: 0,
      fnfNoticeRecovery: 0,
      leaveEncashCount: 0,
      leaveEncashDays: 0,
      attScope: 0,
      attProcessed: 0,
      attOpen: 0,
      attLocked: 0,
      gratuityProvision: c.registerExtras?.gratuityProvision || 0,
      revisionArrears: c.registerExtras?.revisionArrears || 0,
      loanDeductions: c.registerExtras?.loanDeductions || 0,
    }));
  }
  return (analytics?.series || analytics?.trendSeries || []).map((s) => ({
    payMonth: s.payMonth,
    status: s.status,
    headcount: s.headcount || 0,
    fixedCost: s.fixedCost || 0,
    totalCost: s.totalCost || 0,
    totalCashFlow: s.totalCashFlow || 0,
    netPay: s.netPay || 0,
    grossPay: 0,
    tds: s.tds || 0,
    pfEmployer: 0,
    esiEmployer: 0,
    pfEmployee: 0,
    esiEmployee: 0,
    professionalTax: 0,
    statutoryLiabilities: 0,
    avgFixedCost: 0,
    avgEarnedCost: 0,
    taxOld: 0,
    taxNew: 0,
    tdsOld: 0,
    tdsNew: 0,
    pfApplicable: 0,
    esiApplicable: 0,
    revisionsCount: 0,
    revisionsCtcDelta: 0,
    statutoryChangeCount: 0,
    fnfCount: 0,
    fnfNet: 0,
    fnfLeaveEncashment: 0,
    fnfGratuity: 0,
    fnfNoticePay: 0,
    fnfNoticeRecovery: 0,
    leaveEncashCount: 0,
    leaveEncashDays: 0,
    attScope: 0,
    attProcessed: 0,
    attOpen: 0,
    attLocked: 0,
    gratuityProvision: 0,
    revisionArrears: 0,
    loanDeductions: 0,
  }));
}

export function cellValue(row, col) {
  if (typeof col.format === "function") return col.format(row);
  const v = row[col.key];
  return v == null ? "" : v;
}
